import { useEffect, useState } from "react";
import { Bell, BellOff, CheckCheck, X, Smartphone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { http } from "@/lib/http";
import { useCouple } from "@/hooks/useCouple";
import { usePush } from "@/hooks/usePush";
import { cn } from "@/lib/utils";

const ACTION_LABEL = {
  "added_transaction": "adicionou um lançamento",
  "paid_bill": "pagou conta",
  "created_goal": "criou uma meta",
  "celebrated_goal": "comemorou uma meta",
  "added_memory": "colou uma memória",
  "checked_shopping_item": "marcou no mercado",
  "added_agreement": "firmou um acordo",
  "created_weekly_checkin": "fez o check-in da semana",
  "created_monthly_review": "escreveu o fechamento",
  "added_recurring_expense": "criou conta fixa",
  "added_shopping_list": "criou lista de compras",
};

const relTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "agora";
  if (s < 3600) return `há ${Math.floor(s / 60)} min`;
  if (s < 86400) return `há ${Math.floor(s / 3600)}h`;
  if (s < 604800) return `há ${Math.floor(s / 86400)}d`;
  return d.toLocaleDateString("pt-BR");
};

const brl = (v) => `R$ ${Number(v).toFixed(2).replace(".", ",")}`;

export const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const { nameOf, me } = useCouple();
  const qc = useQueryClient();
  const push = usePush();

  const { data } = useQuery({
    queryKey: ["activities"],
    queryFn: async () => (await http.get("/activities?limit=30")).data,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });

  const markSeen = useMutation({
    mutationFn: async () => http.post("/activities/mark-seen", {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["activities"] }),
  });

  const unseen = data?.unseen || 0;
  const items = data?.items || [];

  useEffect(() => {
    if (open && unseen > 0) {
      const t = setTimeout(() => markSeen.mutate(), 800);
      return () => clearTimeout(t);
    }
  }, [open, unseen]); // eslint-disable-line

  const togglePush = async () => {
    if (push.subscribed) {
      await push.disable();
      toast("Notificações desativadas neste dispositivo");
    } else {
      const r = await push.enable();
      if (r.ok) toast.success("Notificações ativadas neste dispositivo");
      else if (r.reason === "denied") toast.error("Permissão negada — libere nas configurações do navegador");
      else if (r.reason === "not_supported") toast.error("Seu navegador não suporta push notifications");
      else toast.error("Não consegui ativar as notificações");
    }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className="w-11 h-11 rounded-full flex items-center justify-center text-[var(--coffee-medium)] hover:bg-[var(--cream-surface)] transition-colors relative" aria-label="notificações" data-testid="notifications-bell">
        <Bell size={20} />
        {unseen > 0 && (
          <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--coral)] text-white text-[10px] font-bold flex items-center justify-center num" data-testid="notifications-unseen-count">
            {unseen > 9 ? "9+" : unseen}
          </span>
        )}
      </button>
      <AnimatePresence>
        {open && (
          <>
            <motion.div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={() => setOpen(false)} data-testid="notifications-backdrop" />
            <motion.div className="fixed inset-x-0 bottom-0 z-[70] mx-auto max-w-md rounded-t-3xl bg-[var(--cream)] p-5 max-h-[80vh] overflow-y-auto"
              initial={{y:"100%"}} animate={{y:0}} exit={{y:"100%"}} transition={{type:"spring",stiffness:260,damping:30}} data-testid="notifications-panel">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="eyebrow">o que rolou</p>
                  <h2 className="serif text-2xl text-[var(--coffee)]">Nossas atualizações</h2>
                </div>
                <button onClick={() => setOpen(false)} className="w-10 h-10 rounded-full hover:bg-[var(--cream-surface)]" aria-label="fechar" data-testid="notifications-close"><X size={20} className="mx-auto" /></button>
              </div>

              {push.supported && (
                <button onClick={togglePush} disabled={push.loading} className={cn("w-full paper px-4 py-3 mb-3 flex items-center gap-3", push.subscribed && "ring-1 ring-[var(--teal)]")} data-testid="notifications-toggle-push">
                  <span className={cn("w-9 h-9 rounded-full flex items-center justify-center shrink-0", push.subscribed ? "bg-[var(--teal)] text-white" : "bg-[var(--cream-surface)] text-[var(--coffee-medium)]")}>
                    {push.subscribed ? <Smartphone size={16} /> : <BellOff size={16} />}
                  </span>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-semibold text-[var(--coffee)]">{push.subscribed ? "Notificações ligadas neste dispositivo" : "Ligar notificações no celular"}</p>
                    <p className="text-xs text-[var(--coffee-muted)]">{push.subscribed ? "toque pra desligar aqui" : "avisa mesmo com o app fechado"}</p>
                  </div>
                </button>
              )}

              {items.length === 0 ? (
                <div className="text-center py-10 text-[var(--coffee-muted)]">
                  <Bell size={28} className="mx-auto mb-2 opacity-40" />
                  <p className="text-sm italic">nada ainda por aqui</p>
                </div>
              ) : (
                <ul className="space-y-2" data-testid="notifications-list">
                  {items.map((it) => {
                    const mine = it.actor_id === me?.id;
                    const isUnseen = !mine && !(it.seen_by || []).includes(me?.id);
                    const actorLabel = mine ? "Você" : (it.actor_name || nameOf(it.actor_id) || "seu parceiro");
                    const actionText = ACTION_LABEL[it.action] || it.action.replace(/_/g, " ");
                    return (
                      <li key={it.id} className={cn("paper px-4 py-3 flex items-start gap-3", isUnseen && "ring-1 ring-[var(--teal)]")} data-testid={`notification-item-${it.id}`} data-mine={mine} data-unseen={isUnseen}>
                        <span className={cn("w-2 h-2 rounded-full mt-2 shrink-0", isUnseen ? "bg-[var(--teal)]" : "bg-[var(--line)]")} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm">
                            <span className="font-semibold">{actorLabel}</span> {actionText}
                            {it.target_label && <span className="text-[var(--coffee-muted)]"> · {it.target_label}</span>}
                            {it.amount != null && <span className="text-[var(--coffee)] font-semibold"> · {brl(it.amount)}</span>}
                          </p>
                          <p className="text-xs text-[var(--coffee-muted)] mt-0.5 num">{relTime(it.created_at)}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              {unseen > 0 && (
                <button onClick={() => markSeen.mutate()} className="btn-ghost w-full mt-4 flex items-center justify-center gap-2 text-[var(--teal)]" data-testid="notifications-mark-all-seen">
                  <CheckCheck size={14} /> marcar tudo como visto
                </button>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default NotificationBell;
